import { useTranslation } from 'react-i18next'
import { Alert, CircularProgress, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { DEVICES } from '@/constants/medconnect-devices-types'

import { getDeviceSettingsSpec } from '../../model/device-settings-spec'
import { useDeviceVerification } from '../../model/useDeviceVerification'

export type VerifyDeviceStepProps = {
  deviceName: DEVICES
}

/** Last step: prove the saved device actually talks to the connector before the user leaves. */
export default function VerifyDeviceStep({ deviceName }: VerifyDeviceStepProps) {
  const { t } = useTranslation()
  const spec = getDeviceSettingsSpec(deviceName)
  const isLiveDevice = spec.connection === 'bluetooth' || spec.connection === 'serial'
  const status = useDeviceVerification(isLiveDevice ? deviceName : null)

  if (!isLiveDevice) {
    return (
      <Stack spacing={2} alignItems="center" py={2}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6">{t('page.configuration.MedconnectDevice.wizard.verify.configured')}</Typography>
        <Typography color="text.secondary" align="center">{t(`page.configuration.MedconnectDevice.wizard.verify.configuredHint.${spec.connection}`)}</Typography>
      </Stack>
    )
  }

  if (status === 'connected') {
    return (
      <Stack spacing={2} alignItems="center" py={2}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6">{t('page.configuration.MedconnectDevice.wizard.verify.connected')}</Typography>
        <Typography color="text.secondary" align="center">{t('page.configuration.MedconnectDevice.wizard.verify.connectedHint')}</Typography>
      </Stack>
    )
  }

  if (status === 'timeout') {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">{t('page.configuration.MedconnectDevice.wizard.verify.timeout')}</Alert>
        <Typography color="text.secondary">{t(`page.configuration.MedconnectDevice.wizard.verify.timeoutHint.${spec.connection}`)}</Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={2} alignItems="center" py={2}>
      <CircularProgress />
      <Typography variant="h6">{t('page.configuration.MedconnectDevice.wizard.verify.waiting')}</Typography>
      <Typography color="text.secondary" align="center">{t(`page.configuration.MedconnectDevice.wizard.verify.waitingHint.${spec.connection}`)}</Typography>
    </Stack>
  )
}
